import { useState } from "react";
import {
	Box,
	Grid,
	Paper,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
} from "@mui/material";
import TableRowsOutlinedIcon from "@mui/icons-material/TableRowsOutlined";
import ViewAgendaOutlinedIcon from "@mui/icons-material/ViewAgendaOutlined";
import { GridColDef } from "@mui/x-data-grid";
import { Dispatch, SetStateAction } from "react";
import { useGridApiRef } from "@mui/x-data-grid";
import { GridApiCommunity } from "@mui/x-data-grid/internals";
import ItemTable from "../../components/ItemTable";
import { Item } from "../../util/Item";
import { updateActiveTask, Util } from "../../util/Util";
import { MYSQL_LIMITS } from "../../util/constants";
import { useSale, SaleTask } from "./SaleContext";
import SaleDetail from "./SaleDetail";
import PaymentDetail from "./PaymentDetail";
import PosCatalogGrid from "./PosCatalogGrid";
import PosCartList from "./PosCartList";
import { useUI } from "../../providers/MoleUIContext";
import { useAppSocket } from "../../providers/AppSocketContext";
import {
	PosCartViewMode,
	readStoredPosCartViewMode,
	writeStoredPosCartViewMode,
} from "./posLayouts";

import { addMoney } from "../../util/quantity";
interface Props {
	renderChildren: boolean;
	setValidateDetail: (value: boolean) => void;
	bundle?: any;
	columns: GridColDef<Item>[];
	allowPosAliasing: boolean;
	totalEnteredAmount: number;
	apiRef: ReturnType<typeof useGridApiRef<GridApiCommunity>>;
	setPendingTasks: Dispatch<SetStateAction<SaleTask<Item>[]>>;
	getMinSellPrice?: (row: Item) => number;
}

const PosVisualLayout = ({
	renderChildren,
	setValidateDetail,
	bundle,
	columns,
	allowPosAliasing,
	totalEnteredAmount,
	apiRef,
	setPendingTasks,
	getMinSellPrice,
}: Props) => {
	const { activeTask, items } = useSale();
	const { t } = useUI();
	const { appSettings } = useAppSocket();
	const [cartView, setCartView] = useState<PosCartViewMode>(() => readStoredPosCartViewMode());

	const cartLines = activeTask?.items ?? [];
	const cartCount = cartLines.length;
	const cartTotal = cartLines.reduce((sum, row) => addMoney(sum, (Number(row.total) || 0)), 0);
	const currency = appSettings?.BIZ_CURRENCY?.value || "USD";

	const handleCartViewChange = (_: unknown, next: PosCartViewMode | null) => {
		if (!next) return;
		setCartView(next);
		writeStoredPosCartViewMode(next);
	};

	return (
		<Grid container spacing={1} sx={{ height: "100%", minHeight: 0 }}>
			<Grid
				size={{ xs: 12, md: 8 }}
				sx={{ display: "flex", flexDirection: "column", minHeight: 0, gap: 1, height: { md: "100%" } }}
			>
				<Paper elevation={24} sx={{ flexShrink: 0 }}>
					{renderChildren && (
						<SaleDetail setValidateCheck={setValidateDetail} />
					)}
				</Paper>
				<Paper
					elevation={24}
					sx={{
						flex: 1,
						minHeight: 220,
						display: "flex",
						flexDirection: "column",
						overflow: "hidden",
						p: 1,
					}}
				>
					{renderChildren && (
						<PosCatalogGrid bundle={bundle} showBarcode />
					)}
				</Paper>

				{/* Cart: table (default) or modern list */}
				{renderChildren && (
					<Paper
						elevation={24}
						sx={{
							flexShrink: 0,
							maxHeight: { xs: 280, md: 300 },
							display: "flex",
							flexDirection: "column",
							overflow: "hidden",
						}}
					>
						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: "1fr auto 1fr",
								alignItems: "center",
								gap: 1,
								px: 1.25,
								py: 0.75,
								borderBottom: 1,
								borderColor: "divider",
								flexShrink: 0,
							}}
						>
							<Typography variant="subtitle2" fontWeight={700} sx={{ justifySelf: "start" }}>
								{t("common:pos.cart.title")}
							</Typography>
							<Typography
								variant="body2"
								fontWeight={700}
								sx={{
									justifySelf: "center",
									textAlign: "center",
									color: "text.primary",
									px: 1,
								}}
							>
								{t("common:ui.labels.count", {
									num: Util.formatNumber(cartCount),
									total: Util.formatCurrency(cartTotal, currency),
								})}
							</Typography>
							<Box sx={{ justifySelf: "end" }}>
								<ToggleButtonGroup
									exclusive
									size="small"
									value={cartView}
									onChange={handleCartViewChange}
									aria-label={t("common:pos.cart.viewToggleAria")}
								>
									<ToggleButton value="table" aria-label={t("common:pos.cart.table")}>
										<Tooltip title={t("common:pos.cart.table")}>
											<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
												<TableRowsOutlinedIcon fontSize="small" />
												{t("common:pos.cart.table")}
											</Box>
										</Tooltip>
									</ToggleButton>
									<ToggleButton value="list" aria-label={t("common:pos.cart.list")}>
										<Tooltip title={t("common:pos.cart.list")}>
											<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
												<ViewAgendaOutlinedIcon fontSize="small" />
												{t("common:pos.cart.list")}
											</Box>
										</Tooltip>
									</ToggleButton>
								</ToggleButtonGroup>
							</Box>
						</Box>

						<Box sx={{ flex: 1, minHeight: 0, overflow: "auto", p: cartView === "list" ? 1 : 0 }}>
							{cartView === "table" ? (
								<ItemTable<Item, SaleTask<Item>>
									activeTask={activeTask}
									setPendingTasks={setPendingTasks}
									columns={columns}
									itemConstructor={Item}
									bundle={bundle}
									items={items}
									lineNameMode="sale"
									allowPosAliasing={allowPosAliasing}
									apiRef={apiRef}
									hideFooter
									getMinSellPrice={getMinSellPrice}
								/>
							) : (
								<PosCartList bundle={bundle} setPendingTasks={setPendingTasks} />
							)}
						</Box>
					</Paper>
				)}

				<Paper elevation={24} sx={{ flexShrink: 0 }}>
					<TextField
						id="details"
						size="small"
						label={t("common:ui.labels.notes")}
						variant="filled"
						fullWidth
						type="text"
						value={activeTask?.description || ""}
						inputProps={{ maxLength: MYSQL_LIMITS.TEXT.maxLength }}
						onChange={(e) => updateActiveTask(setPendingTasks, { description: e.target.value })}
						disabled={!activeTask}
					/>
				</Paper>
			</Grid>
			<Grid size={{ xs: 12, md: 4 }} sx={{ minHeight: 0, height: { md: "100%" }, overflow: "auto" }}>
				{renderChildren && (
					<PaymentDetail totalEnteredAmount={totalEnteredAmount} />
				)}
			</Grid>
		</Grid>
	);
};

export default PosVisualLayout;
